import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, META, getMeta, setMeta, type LocalHousehold } from '@/db/db';
import type { LocalBaby } from '@/lib/schemas';
import { FORMULA_WARN_ML } from '@/lib/schemas';
import { cloudConfigured } from '@/auth/client';
import { applyAccountSession, initializeAuth } from '@/auth/account';
import { assertActiveHousehold } from '@/db/repo';
import { startSync } from '@/sync/runtime';

export const DEFAULT_TZ: string =
  (import.meta.env.VITE_DEFAULT_TIMEZONE as string | undefined) || 'Australia/Sydney';

export interface SessionSnapshot {
  userId: string;
  email: string | null;
}

interface AppState {
  loading: boolean;
  household: LocalHousehold | null;
  babies: LocalBaby[];
  baby: LocalBaby | null;
  tz: string;
  session: SessionSnapshot | null;
  authError?: string;
  setActiveBaby: (id: string) => Promise<void>;
  formulaWarningMl: number;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [authError, setAuthError] = useState<string>();
  const [authReady, setAuthReady] = useState<number | null>(null);
  useEffect(() => { if (cloudConfigured && authReady !== null) return startSync(); }, [authReady]);
  useEffect(() => {
    if (cloudConfigured) return initializeAuth(setAuthError, setAuthReady);
    void applyAccountSession(null).then(async () => setAuthReady(await getMeta<number>(META.authGeneration, 0))).catch(e => setAuthError(String(e)));
  }, []);
  const data = useLiveQuery(async () => {
    const [babies, metaRows] = await Promise.all([
      db.babies.toArray(),
      db.meta.bulkGet([META.household, META.activeBabyId, META.session, META.settings, META.authGeneration]),
    ]);
    return {
      babies: babies.filter((b) => !b.deleted_at && b.household_id === (metaRows[0]?.value as LocalHousehold | undefined)?.id).sort((a, b) => a.created_at.localeCompare(b.created_at)),
      household: (metaRows[0]?.value as LocalHousehold | undefined) ?? null,
      activeId: (metaRows[1]?.value as string | undefined) ?? null,
      session: (metaRows[2]?.value as SessionSnapshot | undefined) ?? null,
      formulaWarningMl: (metaRows[3]?.value as { formulaWarningMl?: number } | undefined)?.formulaWarningMl ?? FORMULA_WARN_ML,
      authGeneration: (metaRows[4]?.value as number | undefined) ?? 0,
      // Tag the query with the cleanup readiness it started after. A retained
      // result from before cleanup must never reveal a prior account cache.
      validatedAt: authReady,
    };
  }, [authReady]);

  const setActiveBaby = useCallback(async (id: string) => {
    await db.transaction('rw', db.meta, db.babies, async () => {
      const baby = await db.babies.get(id);
      if (!baby || baby.deleted_at) throw new Error('Unknown baby');
      await assertActiveHousehold(baby.household_id);
      await setMeta(META.activeBabyId, id);
    });
  }, []);

  const value = useMemo<AppState>(() => {
    if (!data || authReady === null || data.validatedAt !== authReady || data.authGeneration < authReady) {
      return { loading: true, household: null, babies: [], baby: null, tz: DEFAULT_TZ, session: null, setActiveBaby, formulaWarningMl: FORMULA_WARN_ML, authError };
    }
    const baby = data.babies.find((b) => b.id === data.activeId) ?? data.babies[0] ?? null;
    return {
      loading: false,
      household: data.household,
      babies: data.babies,
      baby,
      tz: baby?.timezone ?? DEFAULT_TZ,
      session: data.session,
      setActiveBaby,
      formulaWarningMl: data.formulaWarningMl,
      authError,
    };
  }, [data, setActiveBaby, authError, authReady]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside AppProvider');
  return v;
}

/** Wall clock that re-renders every `intervalMs`. Timers derive from timestamps, never from accumulated ticks. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    const onVis = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs]);
  return now;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
